import { SETTINGS_NAV_ITEMS } from '@constants/Settings'
import { ISettings, ISettingsItem } from '@dataTypes/settings.type'
import { StaffScope } from '@constants/Settings/staffPermission.const'

/**
 * Settings sub-items gated behind a specific PMS scope. This is the single
 * source of truth shared by the nav (via `useSettings`) and the router (via
 * `ScopeRoute` in AppRoutes) so direct route access matches nav visibility.
 * Visibility uses the same scope mechanism the rest of the app relies on
 * (`hasStaffScope` → JWT `scp`), not a hardcoded role — see the Permissions
 * matrix in Settings → Team.
 */
export const SCOPED_SETTINGS_ITEMS: Record<string, StaffScope | StaffScope[]> =
  {
    'clinical-dicom-capture': StaffScope.SETTINGS_WRITE,
    'clinical-patient-form-groups': [
      StaffScope.SETTINGS_READ,
      StaffScope.SETTINGS_WRITE,
    ],
  }

export interface SettingsRouteEntry {
  id: string
  name: string
  description: string
  parentId: string
  fullPath: string
  relativePath: string
}

const SETTINGS_BASE_PATH = '/settings'

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const toRelativePath = (fullPath: string): string =>
  fullPath.replace(`${SETTINGS_BASE_PATH}/`, '')

const toLeafRoute = (
  section: ISettings,
  item: ISettingsItem,
): SettingsRouteEntry => {
  const sectionSlug = toSlug(section.name)
  const itemSlug = toSlug(item.name)
  const fullPath = `${SETTINGS_BASE_PATH}/${sectionSlug}/${itemSlug}`

  return {
    id: item.id,
    name: item.name,
    description: item.description,
    parentId: section.id,
    fullPath,
    relativePath: toRelativePath(fullPath),
  }
}

const toTopLevelRoute = (section: ISettings): SettingsRouteEntry => {
  const sectionSlug = toSlug(section.name)
  const fullPath = `${SETTINGS_BASE_PATH}/${sectionSlug}`

  return {
    id: section.id,
    name: section.name,
    description: section.description,
    parentId: section.id,
    fullPath,
    relativePath: toRelativePath(fullPath),
  }
}

const buildSettingsRouteEntries = (): SettingsRouteEntry[] =>
  SETTINGS_NAV_ITEMS.flatMap((section) =>
    section.items.length > 0
      ? section.items.map((item) => toLeafRoute(section, item))
      : [toTopLevelRoute(section)],
  )

export const SETTINGS_ROUTE_ENTRIES = buildSettingsRouteEntries()

export const SETTINGS_DEFAULT_PATH =
  SETTINGS_ROUTE_ENTRIES.find((entry) => entry.id === 'integrations-settings')
    ?.fullPath ??
  SETTINGS_ROUTE_ENTRIES[0]?.fullPath ??
  SETTINGS_BASE_PATH

const normalizePathname = (pathname: string): string => {
  if (!pathname) {
    return SETTINGS_BASE_PATH
  }
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname
}

export const getSettingsRouteById = (
  id: string,
): SettingsRouteEntry | undefined =>
  SETTINGS_ROUTE_ENTRIES.find((entry) => entry.id === id)

export const getSettingsRouteByPathname = (
  pathname: string,
): SettingsRouteEntry | undefined => {
  const normalizedPathname = normalizePathname(pathname)
  const exactMatch = SETTINGS_ROUTE_ENTRIES.find(
    (entry) => normalizePathname(entry.fullPath) === normalizedPathname,
  )
  if (exactMatch) {
    return exactMatch
  }

  return SETTINGS_ROUTE_ENTRIES.filter((entry) =>
    normalizedPathname.startsWith(`${normalizePathname(entry.fullPath)}/`),
  ).sort(
    (entryA, entryB) =>
      normalizePathname(entryB.fullPath).length -
      normalizePathname(entryA.fullPath).length,
  )[0]
}
